import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  NJAccordion,
  NJAccordionItem,
  NJAccordionItemContent,
  NJAccordionItemHeader,
  NJFormItem,
  NJIcon,
  NJIconButton,
  NJInputSearch,
  NJSpinner,
  NJTag,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { useAgents } from "@/features/agents/useAgents";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { Session } from "@/types";
import {
  useCloseSession,
  useDeleteSession,
  useReopenSession,
  useSessions,
  useUpdateSession,
} from "./useSessions";

function SessionListItem({
  session,
  agentName,
  selected,
  onSelect,
}: {
  session: Session;
  agentName: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(session.title);
  const updateSession = useUpdateSession();
  const closeSession = useCloseSession();
  const reopenSession = useReopenSession();
  const deleteSession = useDeleteSession();

  const submitRename = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed || trimmed === session.title) {
      setRenaming(false);
      return;
    }
    updateSession.mutate(
      { sessionId: session.id, payload: { title: trimmed } },
      { onSuccess: () => setRenaming(false) },
    );
  };

  return (
    <div
      className={`group flex flex-col gap-1 rounded-lg border p-2 transition ${
        selected
          ? "border-nj-brand-moderate-default bg-nj-brand-secondary-default"
          : "border-transparent hover:bg-nj-neutral-secondary-default"
      }`}
    >
      {renaming ? (
        <form
          onSubmit={submitRename}
          onClick={(event) => event.stopPropagation()}
        >
          <NJFormItem
            id={`session-title-${session.id}`}
            label="Rename session"
            labelKind="floating"
            value={title}
            onChange={(event) =>
              setTitle((event.target as HTMLInputElement).value)
            }
            onBlur={submitRename}
          />
        </form>
      ) : (
        <button
          type="button"
          onClick={onSelect}
          className="flex min-w-0 flex-col items-start gap-0.5 text-left"
        >
          <span className="w-full truncate font-nj-bold text-nj-neutral-primary-default">
            {session.title}
          </span>
          <span className="flex items-center gap-1.5">
            <NJText
              as="span"
              scale="xs"
              className="text-nj-neutral-secondary-default"
            >
              {agentName}
            </NJText>
            {session.status !== "ACTIVE" && (
              <NJTag label="Closed" variant="grey" size="xs" />
            )}
          </span>
        </button>
      )}

      <div className="flex items-center gap-0.5 opacity-0 transition group-hover:opacity-100 [&:has(:focus)]:opacity-100">
        <NJIconButton
          icon="push_pin"
          label={session.isPinned ? "Unpin" : "Pin"}
          size="xs"
          ariaPressed={session.isPinned}
          onClick={() =>
            updateSession.mutate({
              sessionId: session.id,
              payload: { isPinned: !session.isPinned },
            })
          }
        />
        <NJIconButton
          icon="edit"
          label="Rename"
          size="xs"
          onClick={() => setRenaming(true)}
        />
        {session.status === "ACTIVE" ? (
          <NJIconButton
            icon="lock"
            label="Close session"
            size="xs"
            onClick={() => closeSession.mutate(session.id)}
          />
        ) : (
          <NJIconButton
            icon="lock_open"
            label="Reopen session"
            size="xs"
            onClick={() => reopenSession.mutate(session.id)}
          />
        )}
        <NJIconButton
          icon="delete"
          label="Delete session"
          size="xs"
          onClick={() => deleteSession.mutate(session.id)}
        />
      </div>
    </div>
  );
}

/** Session history (in the app sidebar) — search + pinned-first list; selecting opens /chat/:id. */
export function SessionSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeId = pathname.startsWith("/chat/")
    ? decodeURIComponent(pathname.slice("/chat/".length))
    : null;

  const [searchInput, setSearchInput] = useState("");
  const query = useDebouncedValue(searchInput).toLowerCase();
  const { data: sessions, isLoading } = useSessions();
  // Resolve each session's agent slug to its display name. `limit` must stay within the API cap
  // (100); only fetched when there is at least one session to label.
  const { data: agents } = useAgents(
    { limit: 100 },
    { enabled: (sessions?.length ?? 0) > 0 },
  );

  const agentNameBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const agent of agents?.items ?? []) map.set(agent.slug, agent.name);
    return map;
  }, [agents]);

  const visible = useMemo(() => {
    const list = sessions ?? [];
    const filtered = query
      ? list.filter((session) => session.title.toLowerCase().includes(query))
      : list;
    return [...filtered].sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
  }, [sessions, query]);

  // Pinned sessions are shown apart, above the per-agent groups (legacy parity).
  const pinned = useMemo(
    () => visible.filter((session) => session.isPinned),
    [visible],
  );

  // Group the remaining (unpinned) sessions by agent (agents ordered by most-recent activity).
  const groups = useMemo(() => {
    const map = new Map<string, Session[]>();
    for (const session of visible) {
      if (session.isPinned) continue;
      const bucket = map.get(session.agentId) ?? [];
      bucket.push(session);
      map.set(session.agentId, bucket);
    }
    return Array.from(map.entries()).map(([agentId, agentSessions]) => ({
      agentId,
      name: agentNameBySlug.get(agentId) ?? agentId,
      sessions: agentSessions,
    }));
  }, [visible, agentNameBySlug]);

  // Keep the group holding the active conversation expanded.
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  useEffect(() => {
    if (!activeId) return;
    const active = (sessions ?? []).find((session) => session.id === activeId);
    if (active) setExpandedAgent(active.agentId);
  }, [activeId, sessions]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 px-3">
      <NJText
        as="p"
        scale="xs"
        className="font-nj-bold uppercase tracking-wide text-nj-neutral-tertiary-default"
      >
        History
      </NJText>
      <NJInputSearch
        label="Search sessions"
        placeholder="Search"
        value={searchInput}
        onChange={(event) => setSearchInput(event.currentTarget.value)}
      />
      {isLoading ? (
        <div className="flex justify-center py-6">
          <NJSpinner size="sm" aria-label="Loading sessions" />
        </div>
      ) : groups.length === 0 && pinned.length === 0 ? (
        <NJText as="p" scale="sm" className="text-nj-neutral-secondary-default">
          No sessions yet.
        </NJText>
      ) : (
        <div className="pt-8 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
          {pinned.length > 0 && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2 px-1">
                <span className="flex items-center gap-1 text-xs font-nj-bold uppercase tracking-wide text-nj-neutral-tertiary-default">
                  <NJIcon name="push_pin" />
                  Pinned
                </span>
                <span className="rounded-full bg-nj-brand-secondary-default px-2 text-xs text-nj-brand-solid-default">
                  {pinned.length}
                </span>
              </div>
              {pinned.map((session) => (
                <SessionListItem
                  key={session.id}
                  session={session}
                  agentName={
                    agentNameBySlug.get(session.agentId) ?? session.agentId
                  }
                  selected={session.id === activeId}
                  onSelect={() => {
                    navigate(`/chat/${session.id}`);
                    onNavigate?.();
                  }}
                />
              ))}
            </div>
          )}
          <NJAccordion>
            {groups.map((group) => (
              <NJAccordionItem
                key={group.agentId}
                name="session-agents"
                open={(expandedAgent ?? groups[0]?.agentId) === group.agentId}
                onToggle={(event) =>
                  setExpandedAgent(
                    event.currentTarget.open ? group.agentId : null,
                  )
                }
              >
                <NJAccordionItemHeader>
                  <span className="flex w-full items-center justify-between gap-2 pr-2">
                    <span className="truncate">{group.name}</span>
                    <span className="rounded-full bg-nj-brand-secondary-default px-2 text-xs text-nj-brand-solid-default">
                      {group.sessions.length}
                    </span>
                  </span>
                </NJAccordionItemHeader>
                <NJAccordionItemContent>
                  <div className="flex flex-col gap-1 py-1">
                    {group.sessions.map((session) => (
                      <SessionListItem
                        key={session.id}
                        session={session}
                        agentName={group.name}
                        selected={session.id === activeId}
                        onSelect={() => {
                          navigate(`/chat/${session.id}`);
                          onNavigate?.();
                        }}
                      />
                    ))}
                  </div>
                </NJAccordionItemContent>
              </NJAccordionItem>
            ))}
          </NJAccordion>
        </div>
      )}
    </div>
  );
}
